import { generateSpeech } from './gemini';

// Comprehensive mapping of language codes to BCP-47 speech synthesis tags
export const getSpeechLocale = (langCode?: string): string => {
  if (!langCode) return 'en-US';
  const code = langCode.toLowerCase().trim();

  const localeMap: Record<string, string> = {
    // English & Indian Languages
    en: 'en-US',
    hi: 'hi-IN',
    te: 'te-IN',
    ta: 'ta-IN',
    mr: 'mr-IN',
    gu: 'gu-IN',
    bn: 'bn-IN',
    kn: 'kn-IN',
    ml: 'ml-IN',
    pa: 'pa-IN',
    ur: 'ur-PK',
    or: 'or-IN',
    as: 'as-IN',
    ks: 'ks-IN',
    sd: 'sd-IN',
    ne: 'ne-NP',

    // Major Global Languages
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    ar: 'ar-SA',
    'zh-cn': 'zh-CN',
    'zh-tw': 'zh-TW',
    zh: 'zh-CN',
    ja: 'ja-JP',
    ko: 'ko-KR',
    pt: 'pt-BR',
    ru: 'ru-RU',
    it: 'it-IT',
    id: 'id-ID',
    tr: 'tr-TR',
    vi: 'vi-VN',
    th: 'th-TH',
    nl: 'nl-NL',
    pl: 'pl-PL',
    fa: 'fa-IR',
    uk: 'uk-UA',
    ro: 'ro-RO',
    el: 'el-GR',
    cs: 'cs-CZ',
    sv: 'sv-SE',
    hu: 'hu-HU',
    da: 'da-DK',
    fi: 'fi-FI',
    no: 'nb-NO',
    sk: 'sk-SK',
    hr: 'hr-HR',
    bg: 'bg-BG',
    ms: 'ms-MY',
    am: 'am-ET',
    my: 'my-MM',
    km: 'km-KH',
    si: 'si-LK',
    af: 'af-ZA',
    he: 'he-IL',
    sw: 'sw-KE',
    fil: 'fil-PH',
    tl: 'fil-PH',
    az: 'az-AZ',
    bs: 'bs-BA',
    ca: 'ca-ES',
    et: 'et-EE',
    gl: 'gl-ES',
    ha: 'ha-NG',
    is: 'is-IS',
    jv: 'jv-ID',
    kk: 'kk-KZ',
    ku: 'ku-TR',
    ky: 'ky-KG',
    lo: 'lo-LA',
    lt: 'lt-LT',
    lv: 'lv-LV',
    mk: 'mk-MK',
    mn: 'mn-MN',
    ps: 'ps-AF',
    sl: 'sl-SI',
    so: 'so-SO',
    sq: 'sq-AL',
    sr: 'sr-RS',
    uz: 'uz-UZ',
    zu: 'zu-ZA'
  };

  return localeMap[code] || (code.length === 2 ? `${code}-${code.toUpperCase()}` : langCode);
};

// Clean text for natural speech synthesis
export const cleanTextForSpeech = (text: string): string => {
  if (!text) return '';
  return text
    // Replace markdown links [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove raw URLs
    .replace(/https?:\/\/\S+/gi, '')
    // Remove markdown formatting characters
    .replace(/[*_`#~>]/g, ' ')
    // Replace multiple spaces and line breaks with single pause
    .replace(/\s+/g, ' ')
    .trim();
};

type SpeechListener = (activeId: string | null, state: 'idle' | 'loading' | 'playing') => void;

class SpeechManager {
  private activeId: string | null = null;
  private state: 'idle' | 'loading' | 'playing' = 'idle';
  private listeners: Set<SpeechListener> = new Set();
  private currentAudio: HTMLAudioElement | null = null;
  private keepAliveTimer: any = null;

  subscribe(listener: SpeechListener) {
    this.listeners.add(listener);
    listener(this.activeId, this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach(l => l(this.activeId, this.state));
  }

  getActiveId(): string | null {
    return this.activeId;
  }

  getState(): 'idle' | 'loading' | 'playing' {
    return this.state;
  }

  private startKeepAlive() {
    this.stopKeepAlive();
    if (typeof window !== 'undefined') {
      this.keepAliveTimer = setInterval(() => {
        if ('speechSynthesis' in window && window.speechSynthesis.speaking) {
          window.speechSynthesis.pause();
          window.speechSynthesis.resume();
        }
      }, 10000);
    }
  }

  private stopKeepAlive() {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  stop() {
    this.stopKeepAlive();

    // 1. Stop SpeechSynthesis
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try {
        window.speechSynthesis.cancel();
      } catch (e) {
        console.warn("speechSynthesis.cancel error:", e);
      }
    }

    // 2. Stop HTML Audio if playing
    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
      } catch (e) {
        console.warn("audio.pause error:", e);
      }
      this.currentAudio = null;
    }

    this.activeId = null;
    this.state = 'idle';
    this.notify();
  }

  private findVoice(locale: string): SpeechSynthesisVoice | undefined {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return undefined;
    const voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) return undefined;

    const loc = locale.toLowerCase().replace('_', '-');
    const base = loc.split('-')[0];

    // 1. Exact match (e.g. hi-IN)
    const exact = voices.find(v => v.lang.toLowerCase().replace('_', '-') === loc);
    if (exact) return exact;

    // 2. Starts with locale
    const starts = voices.find(v => v.lang.toLowerCase().replace('_', '-').startsWith(loc));
    if (starts) return starts;

    // 3. Base language match (e.g. hi)
    const baseMatch = voices.find(v => v.lang.toLowerCase().replace('_', '-').startsWith(base));
    if (baseMatch) return baseMatch;

    return undefined;
  }

  async speak(id: string, text: string, langCode: string = 'en') {
    const cleaned = cleanTextForSpeech(text);
    if (!cleaned) return;

    // If already playing this ID, stop it (toggle off)
    if (this.activeId === id && (this.state === 'playing' || this.state === 'loading')) {
      this.stop();
      return;
    }

    // Stop any previously playing speech
    this.stop();

    this.activeId = id;
    const locale = getSpeechLocale(langCode);

    // Primary: Web Speech API (speechSynthesis)
    const hasSpeechSynthesis = typeof window !== 'undefined' && 'speechSynthesis' in window;

    if (hasSpeechSynthesis) {
      try {
        window.speechSynthesis.cancel(); // Flush queue

        const utterance = new SpeechSynthesisUtterance(cleaned);
        utterance.lang = locale;
        utterance.rate = 0.95; // Slightly measured rate for maximum readability
        utterance.pitch = 1.0;

        const voice = this.findVoice(locale);
        if (voice) {
          utterance.voice = voice;
        }

        utterance.onstart = () => {
          if (this.activeId === id) {
            this.state = 'playing';
            this.notify();
            this.startKeepAlive();
          }
        };

        utterance.onend = () => {
          if (this.activeId === id) {
            this.stop();
          }
        };

        utterance.onerror = (event) => {
          console.warn("speechSynthesis utterance error:", event);
          // If canceled intentionally, ignore
          if (event.error === 'canceled' || event.error === 'interrupted') {
            return;
          }
          // If synthesis failed, try fallback
          this.fallbackToAudio(id, cleaned, langCode);
        };

        this.state = 'playing';
        this.notify();
        window.speechSynthesis.speak(utterance);
        return;
      } catch (err) {
        console.warn("speechSynthesis initiation failed, falling back to audio:", err);
      }
    }

    // Fallback: Gemini Speech / Cloud Audio
    await this.fallbackToAudio(id, cleaned, langCode);
  }

  private async fallbackToAudio(id: string, text: string, langCode: string) {
    if (this.activeId !== id) return;

    this.state = 'loading';
    this.notify();

    try {
      const audioDataUrl = await generateSpeech(text, langCode);
      if (this.activeId !== id) return;

      if (audioDataUrl) {
        const audio = new Audio(audioDataUrl);
        this.currentAudio = audio;

        audio.onended = () => {
          if (this.activeId === id) {
            this.stop();
          }
        };

        audio.onerror = (e) => {
          console.error("Audio playback error:", e);
          this.stop();
        };

        await audio.play();
        this.state = 'playing';
        this.notify();
      } else {
        this.stop();
      }
    } catch (e) {
      console.error("Audio generation fallback failed:", e);
      this.stop();
    }
  }
}

export const speechManager = new SpeechManager();
